// The card a smart link draws, fetched in batches.
//
// A description can carry dozens of smart links, and a work item's screen
// draws several rich-text fields and every comment. Each card asks for its
// key here; the asks of one moment are gathered into ONE POST
// /api/issues/cards and the answers are kept for a minute, so reopening a
// work item does not refetch them and a status change still shows soon.
//
// useIssueCard(key) → undefined while loading, null when no visible work
// item answers (the link then draws as its plain address), else the card.

import { useEffect, useSyncExternalStore } from 'react';
import api from '@app/services/api';

const TTL_MS = 60 * 1000;
const BATCH_MS = 20;
const MAX_PER_CALL = 100;

const entries = new Map();   // KEY → { card, at }
const listeners = new Map(); // KEY → Set<fn>
const queued = new Set();
const inFlight = new Set();
let timer = null;

function notify(key) {
  for (const fn of listeners.get(key) || []) fn();
}

function settle(key, card) {
  entries.set(key, { card, at: Date.now() });
  inFlight.delete(key);
  notify(key);
}

async function flush() {
  timer = null;
  const keys = [...queued];
  queued.clear();
  for (let i = 0; i < keys.length; i += MAX_PER_CALL) {
    const chunk = keys.slice(i, i + MAX_PER_CALL);
    try {
      const { cards = {} } = await api.getIssueCards(chunk);
      for (const k of chunk) settle(k, cards[k] || null);
    } catch {
      // A failed lookup draws the plain address — never a broken card.
      for (const k of chunk) settle(k, null);
    }
  }
}

function request(key) {
  if (inFlight.has(key)) return;
  inFlight.add(key);
  queued.add(key);
  if (!timer) timer = setTimeout(flush, BATCH_MS);
}

function isFresh(key) {
  const e = entries.get(key);
  return !!e && Date.now() - e.at < TTL_MS;
}

function subscribe(key, fn) {
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(fn);
  return () => listeners.get(key)?.delete(fn);
}

export function useIssueCard(key) {
  const entry = useSyncExternalStore(
    (fn) => (key ? subscribe(key, fn) : () => {}),
    () => (key ? entries.get(key) : undefined),
  );
  useEffect(() => {
    if (key && !isFresh(key)) request(key);
  }, [key]);
  if (!key) return null;
  return entry ? entry.card : undefined;
}
